/**
 * Transmittals Shared Types
 * Platform-agnostic type definitions for transmittal hooks
 *
 * Server data is derived from the route contracts; the rest are the hooks' and components' own form
 * state and view models.
 */

import type { ApiPostResponse, ApiRow, ApiSuccess, ApiSuccessOf } from "@AxiosWrapper";
import type { GetTransmittalDataRequest, SaveTransmittalsRequest } from "@ServerTypes/transmittals/transmittals.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ProjectFormTemplateType } from "@Helpers/projectFormTemplateTypes";

/** `/transmittals/getTransmittalDataById`'s success body: the transmittal (`data`) and its files (`metadata`). */
type TransmittalById = ApiSuccess<"/transmittals/getTransmittalDataById">;

// ============================================================================
// Core Entity Types (Frontend-specific extensions)
// ============================================================================

/**
 * A space the transmittal form offers, as a `/spaces/spacesData` row. The form reads only `_id` and
 * `spaceName`, so the type names exactly those: the list also receives the space picker's own rows
 * (`changeOriginSpace`), which carry both but not the rest of a list row.
 */
export type Space = Pick<ApiRow<"/spaces/spacesData">, "_id" | "spaceName">;

export interface OriginSpace {
  _id: string;
  spaceName: string;
}

export interface SelectedUser {
  id: string; // Composite key: `${userId}_${spaceId}`
  userId?: string;
  spaceId: string;
  spaceName: string;
  userName?: string;
  email?: string;
  organisation?: string;
  designation?: string;
}

export type SpaceWithUsers = TransmittalData["destinationSpace"];

// ============================================================================
// Reference Types
// ============================================================================

/**
 * A file version attached to a transmittal, its display members under the file's stored names. A file
 * picked from the file manager is referenced by its current version, the last of its row's `versions`
 * (its `_id` is `fileVersion`; `fileVersionNumber`, `size`, `createdDate` are that version's). The
 * display columns are optional because their sources really do arrive without them: `fileVersionNumber`
 * / `size` / `fileType` are optional there, `createdDate` is Unix-ms OR an ISO string, and a file has no
 * `remarks` until the transmittal form fills one in. Every reader renders a missing value as a blank cell
 * (`useTransmittalReferences` defaults `remarks`).
 */
export interface TransmittalReference {
  /** A file-version `_id`; sent as `attachedResources[].fileVersionId`. */
  fileVersion: ObjectIdString;
  originalName: string;
  fileVersionNumber?: number | undefined;
  size?: number | undefined;
  createdDate?: number | string | undefined;
  resourceType: string;
  fileType?: string | undefined;
  remarks?: string | undefined;
  transmittalVersionId?: ObjectIdString;
}

/** One references-grid row, projected from a {@link TransmittalReference}; same optional columns. */
export interface ReferenceDatasetItem {
  id: string;
  originalName: string;
  fileVersionNumber?: number | undefined;
  size?: number | undefined;
  createdDate?: number | string | undefined;
  resourceType: string;
  fileType?: string | undefined;
  remarks: string;
}

// ============================================================================
// Save Payload Types (/transmittals/saveTransmittalsData)
// ============================================================================

export type SpaceUserPayload = { spaceId: ObjectIdString; userId: ObjectIdString[] };

export type AttachedResourcePayload = TransmittalPayload["attachedResources"][number];

/**
 * The request schema's OUTPUT type, not `ApiPostRequest` (its input side): only the output keeps the
 * `ObjectId` brand on every id, so a plain string cannot be sent as one.
 */
export type TransmittalPayload = SaveTransmittalsRequest;

export type SaveTransmittalsResponse = ApiPostResponse<"/transmittals/saveTransmittalsData">;

export type SaveTransmittalsData = ApiSuccess<"/transmittals/saveTransmittalsData">["data"];

// ============================================================================
// Response Types (/transmittals/getTransmittalDataById)
// ============================================================================

export type TransmittalData = TransmittalById["data"];

export type TransmittalDetails = TransmittalData;

export type TransmittalMetadata = TransmittalById["metadata"];

export type TransmittalFileMetadata = TransmittalMetadata["files"][number];

export type TransmittalFileSpaceAccess = TransmittalFileMetadata["spaces"][number];

/** Attached file for display in UI */
export interface AttachedFile {
  id: string;
  /** Optional for the same reason as the fields below: `originalName` is optional on the file document. */
  name?: string | undefined;
  // Every optional here is filled from a server metadata field that is itself optional, so the
  // value really can arrive as an explicit `undefined` — the declarations say so.
  fileType?: string | undefined;
  version?: number | undefined;
  size?: number | undefined;
  addedOn?: number | undefined;
  resourceType?: string | undefined;
  isFolder: number;
  toSpaceData?: TransmittalFileSpaceAccess[] | undefined;
  accessDocId?: string | undefined;
  remarks?: string | null | undefined;
  hasNewerVersionInDestination?: boolean | undefined;
  latestVersionInDestination?: TransmittalFileMetadata["latestVersionInDestination"];
}

// ============================================================================
// File Version Types
// ============================================================================

export interface FileVersionData {
  resourceVersionId: string;
  fileVersionNumber: number;
  createdDate: number;
  indexAtProjectLevel: number;
  transmittalId: string;
}

export interface FileVersionCheckResult {
  isLatest: boolean;
  currentVersion: FileVersionData | null;
  latestVersion: FileVersionData | null;
}

// ============================================================================
// Permission Types
// ============================================================================

export interface ModuleAccess {
  ready: boolean;
  /**
   * One of the numeric seat constants from `@Helpers/constants` (PORTAL_FULL_SEAT = 1 /
   * PORTAL_NO_SEAT = 0), as supplied by `useUserModuleAccess`, or `null` before the slices load.
   */
  portalSeat: number | null;
}

/**
 * The project capability object as the store holds it (`state.getProjectSettingObject`, fed by
 * `/projects/getProjectObject/:id`), so a renamed or reshaped capability is a type error here. Create reads
 * `transmittals.create.enable` (TRANSMITTAL_CREATE, W10).
 */
export type ProjectSettings = NonNullable<ApiSuccessOf<"get", "/projects/getProjectObject/:id">["result"]>;

// ============================================================================
// List Query Types
// ============================================================================

/**
 * The where clause `/transmittals/getTransmittalData` accepts, from the request schema's OUTPUT side:
 * every condition runs through a `z.preprocess`, so the input side (`ApiPostRequest`) is `unknown[]`.
 * The server rejects any field outside the list's columns (`transmittalListColumns`, transmittals.schema.ts) at the Zod
 * boundary.
 */
export type TransmittalWhereClause = NonNullable<NonNullable<GetTransmittalDataRequest["value"]>["where"]>;

export interface TransmittalListQuery {
  projectId?: string;
  portalId?: string;
  where?: TransmittalWhereClause;
}

/**
 * The OTHER filter shape a saved view can hold — a bag of per-column conditions written by the grid,
 * not by this feature. It stays open because `layoutView.data.filters` is portal-authored client state
 * with no server schema; `applyViewLayout` only ever counts its keys and hands it straight back.
 */
export interface FilterConditions {
  [key: string]: unknown;
}

export interface ViewLayout {
  data: {
    columns?: string[];
    /** Saved views hold either the grid's condition bag or a ready-made where clause. */
    filters?: FilterConditions | TransmittalWhereClause;
  };
}

// ============================================================================
// Form Template Types
// ============================================================================

/**
 * The form template a transmittal stores. The create form seeds it from `/projects/getFormTemplate`
 * and sends its `_id` + `template` as the save payload's `formTemplate`.
 */
export type FormTemplate = NonNullable<TransmittalData["formTemplate"]>;

// ============================================================================
// Hook Parameter Types
// ============================================================================

export interface UseCreateTransmittalFormParams {
  projectId: string;
  selectedSpaceId?: string | null;
  selectedReferences?: TransmittalReference[] | null;
  /** Sent as `saveTransmittalsRequest.reviewData.reviewId`, so it carries that field's brand. */
  reviewId?: NonNullable<SaveTransmittalsRequest["reviewData"]>["reviewId"] | null;
  mobile?: boolean;
  api: {
    fetchSpaces: (projectId: string) => Promise<Space[]>;
    fetchFormTemplate: (projectId: string, resourceType: ProjectFormTemplateType) => Promise<FormTemplate | null>;
    saveTransmittal: (data: TransmittalPayload) => Promise<{ data: SaveTransmittalsResponse }>;
  };
  callbacks?: {
    /** Receives the response's inner `data` (each new transmittal's `_id` and number), not the envelope. */
    onSuccess?: (response: SaveTransmittalsData) => void;
    onError?: (error: string) => void;
  };
}

export interface UseTransmittalDetailParams {
  transmittalVersionId: string;
  mobile?: boolean;
  api: {
    fetchTransmittal: (id: string) => Promise<void>;
    /** Called with `TransmittalData["_id"]`, the transmittal's `objectIdString` `_id`. */
    checkAccess: (transmittalId: TransmittalData["_id"]) => Promise<void>;
  };
}

export interface UseTransmittalListParams {
  projectId?: string;
  portalId?: string;
  byPortal?: boolean;
  mobile?: boolean;
}

export interface UseTransmittalPermissionsParams {
  projectSettings?: ProjectSettings | null;
  moduleAccess: ModuleAccess;
  byPortal?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

export const RESOURCE_TYPE_FILE = "RESOURCE_TYPE_FILE";
export const RESOURCE_TYPE_MERGED_FILE = "RESOURCE_TYPE_MERGED_FILE";
export const RESOURCE_TYPE_FOLDER = "RESOURCE_TYPE_FOLDER";
